'use client';

import {useMemo} from 'react';
import {
    BrowseNavigationItemType,
    BrowseNavigationItemStyle,
} from '../../../events/browse-navigation-item-selected';
import {Course, CourseCategory, Topic} from '@udemy-v2/browse-course';
import {BadgeClass} from '@/generated/graphql';
import {useBadgeClassesByCourseIdQuery} from '@/generated/graphql';
import {isObject} from '@/lib/utils';

export type NavigationItem =
    | CourseCategory
    | Topic
    | (BadgeClass & {
          class:
              | 'category'
              | 'course_category'
              | 'course_label'
              | 'course_subcategory'
              | 'subcategory'
              | 'topic';
      });

export interface CLPNavigationItem {
    id: string;
    type: BrowseNavigationItemType;
    title: string;
    url: string;
    uiRegion: string;
    style: BrowseNavigationItemStyle;
}

/* Priority order is: Primary topic, secondary topics, cert name (if distinct from cert topic name), subcategory, category */
export const typePriority: {[key in BrowseNavigationItemType]: number} = {
    [BrowseNavigationItemType.TOPIC]: 1,
    [BrowseNavigationItemType.BADGE]: 2,
    [BrowseNavigationItemType.SUBCATEGORY]: 3,
    [BrowseNavigationItemType.CATEGORY]: 4,
    [BrowseNavigationItemType.RECOMMENDATION_COLLECTION]: 5,
};

export const getNavigationItemId = (navigationItem: NavigationItem) => {
    // For BadgeClass, id is badge topic id
    if ('topic' in navigationItem && navigationItem.topic) {
        return String(navigationItem.topic.id);
    }
    return String(navigationItem.id);
};

export const getNavigationItemType = (
    navigationItem: NavigationItem,
): BrowseNavigationItemType | null => {
    // Breadcrumbs include _class field for category, subcategory, topic, and course_label (primary topic)
    if ('_class' in navigationItem) {
        switch (navigationItem._class) {
            case 'category':
            case 'course_category':
                return BrowseNavigationItemType.CATEGORY;
            case 'subcategory':
            case 'course_subcategory':
                return BrowseNavigationItemType.SUBCATEGORY;
            case 'topic':
            case 'course_label':
                return BrowseNavigationItemType.TOPIC;
        }
    }
    // For BadgeClass that contains a topic, type is badge
    if ('topic' in navigationItem && navigationItem.topic) {
        return BrowseNavigationItemType.BADGE;
    }

    // For topics not included in breadcrumbs
    if ('topic_channel_url' in navigationItem) {
        return BrowseNavigationItemType.TOPIC;
    }

    // Shouldn't be able to hit this block, needed for TS
    /**
     * Note: Previously, we were hitting RECOMMENDATION_COLLECTION. We should not return valid items when we don't know
     * what to return so I'm just returning empty string here to make downstream failed if needed.
     */
    return null;
};

export const getNavigationItemHref = (navigationItem: NavigationItem) => {
    // For BadgeClass URL is on nested topic
    if ('topic' in navigationItem && navigationItem.topic) {
        return navigationItem.topic.url;
    }
    // Category / Subcategory
    if ('url' in navigationItem) {
        return navigationItem.url;
    }
    // Topic
    if ('topic_channel_url' in navigationItem) {
        return navigationItem.topic_channel_url;
    }
    // Should be unreachable - needed for TS compilation
    return '';
};

export const getNavigationItemTitle = (navigationItem: NavigationItem) => {
    // For Badge, use name of badge
    if ('topic' in navigationItem && navigationItem.name) {
        return navigationItem.name;
    }
    // For Category / Subcategory, use title
    if ('title' in navigationItem) {
        return navigationItem.title;
    }
    // For Topic, use name
    if ('name' in navigationItem) {
        return navigationItem.name;
    }
    // Should be unreachable - needed for TS compilation
    return '';
};

const addClass = <T,>(classType: string, item: T) => {
    if (isObject(item)) {
        return {_class: classType, ...item};
    }
    return item;
};

export const useCLPNavigationContext = (
    course: Course,
    excludeItems: BrowseNavigationItemType[] = [],
): CLPNavigationItem[] => {
    // Stabilize courseId to prevent unnecessary query re-executions
    const courseId = useMemo(() => (course?.id ? String(course.id) : ''), [course?.id]);

    // Only fetch badge data if we're not excluding badges
    const shouldFetchBadges = !excludeItems.includes(BrowseNavigationItemType.BADGE);

    const {data: badgeData} = useBadgeClassesByCourseIdQuery(
        {courseId},
        {
            enabled: !!courseId && shouldFetchBadges,
        },
    );

    // Process the navigation items when course or badge data changes
    return useMemo(() => {
        if (!course) return [];

        const courseBadge = shouldFetchBadges
            ? (badgeData?.badgeClassesByLearningProducts?.[0] as BadgeClass | undefined)
            : null;

        const topic = addClass('topic', course?.primaryTopic);
        const topics = course?.topics?.map((topic) => addClass('topic', topic)) ?? [];

        const categoriesAndSubcategories =
            course?.categories?.flatMap((category) => [
                addClass('category', category),
                ...(category.subcategories ?? []).map((subcategory) =>
                    addClass('subcategory', subcategory),
                ),
            ]) ?? [];

        return [topic, ...topics, ...categoriesAndSubcategories, courseBadge]
            .filter(Boolean)
            .map((navigationItem) => ({
                id: getNavigationItemId(navigationItem as NavigationItem),
                type: getNavigationItemType(navigationItem as NavigationItem),
                title: getNavigationItemTitle(navigationItem as NavigationItem) as string,
                url: getNavigationItemHref(navigationItem as NavigationItem),
                uiRegion: 'browse_navigation',
                style: BrowseNavigationItemStyle.PILL_LIGHT,
            }))
            .filter(
                (item): item is CLPNavigationItem => item.type !== null && item.title && item.url,
            )
            .filter(
                (navItem, index, items) =>
                    items.findIndex(
                        (navItemCompare) => navItemCompare?.title === navItem?.title,
                    ) === index,
            )
            .sort((a, b) => typePriority[a.type] - typePriority[b.type])
            .slice(0, 5);
    }, [course, badgeData, shouldFetchBadges]);
};
